// MUST stay the first import: it sets Zod's `jitless` before any schema is built, so the
// CSP-blocked `new Function` probe never runs (steward-app#2352). Pinned by
// src/zodJitlessEntry.test.ts. Imported by relative path and distinct from the package index
// because index.ts does not re-export it.
import '../packages/steward-types/src/zodJitless';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { AuthProvider } from 'react-oidc-context';
import { router } from './router';
import { oidcConfig } from './auth/config';
import { ThemeProvider } from './theme/ThemeProvider';
import { BrandProvider } from './brand/BrandProvider';
import { registerPwaUpdates, handleNavigation } from './pwa/pwaUpdate';
import { UpdateToast } from './pwa/UpdateToast';
import { stampVersionMetadata } from './lib/appEnv';
import { prewarmEndSessionEndpoints } from './auth/endSession';
import { onSigninCallbackStamp } from './auth/signedInStamp';

// Stamp <meta app-version/app-env>, window.__APP_VERSION__, and a console banner.
stampVersionMetadata();

// Resolve the pool's OIDC discovery document now, so signing out later never waits on a
// live network call (and never silently falls back to the build-time hosted-UI host, which
// is how docs/29 F15 happened). One small request, fire-and-forget, at app start.
prewarmEndSessionEndpoints();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

// Register the service worker. A waiting update is applied at the user's next
// real navigation (never mid-flow / during the auth callback) — see pwaUpdate.ts.
registerPwaUpdates();
router.subscribe((state) => {
  if (state.navigation.state === 'idle') {
    handleNavigation({
      historyAction: state.historyAction,
      pathname: state.location.pathname,
    });
  }
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <BrandProvider>
        <AuthProvider {...oidcConfig} onSigninCallback={onSigninCallbackStamp}>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
            <UpdateToast />
          </QueryClientProvider>
        </AuthProvider>
      </BrandProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
